import { useEffect, useState } from 'react';
import { CaretRightIcon, StopIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { Button, IconButton } from '@/ui/button/Button';
import classNames from '@/utils/classnames';

import { useAgentSessionStore } from '../../stores/agent-session-store';
import { formatElapsed, type RunningJob } from '../../stores/background-jobs';

const TICK_MS = 1000;

/** The wall clock, re-read every second for as long as the caller is mounted. */
function useNow(): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);
  return now;
}

type KillJobs = (id?: string) => boolean;

function stop(killJobs: KillJobs, id?: string) {
  if (!killJobs(id)) toast.error("Couldn't reach the agent to stop that job.");
}

function JobRow({ job, now, killJobs }: { job: RunningJob; now: number; killJobs: KillJobs }) {
  return (
    <li className="flex items-center gap-2.5">
      <span className="shrink-0 text-xs font-medium text-fg">{job.id}</span>
      <code className="min-w-0 flex-1 truncate font-mono text-2xs text-fg-tertiary" title={job.command}>
        {job.command}
      </code>
      <span className="shrink-0 text-2xs text-fg-tertiary tabular-nums">{formatElapsed(now - job.startedAt)}</span>
      <IconButton size={7} onClick={() => stop(killJobs, job.id)} aria-label={`Stop background job ${job.id}`}>
        <StopIcon size={12} weight="fill" />
      </IconButton>
    </li>
  );
}

/**
 * Mounted only while something is running, so the one-second tick lives exactly as long as there
 * is an elapsed time to show.
 */
function RunningJobsStrip({ jobs, killJobs }: { jobs: RunningJob[]; killJobs: KillJobs }) {
  const now = useNow();
  const [open, setOpen] = useState(false);
  // An idle agent with a job out is waiting on it: the job's end is what starts its next turn.
  const waiting = !useAgentSessionStore((s) => s.turnInFlight);

  const [oldest] = jobs;
  if (!oldest) return null;
  const single = jobs.length === 1;
  const elapsed = formatElapsed(now - oldest.startedAt);

  return (
    <div className="shrink-0 border-t border-line-subtle bg-surface-1 px-6 py-1.5">
      <div className="flex items-center gap-2.5">
        <span className="zooid-pulse size-1.5 shrink-0 rounded-full bg-brand-text" />
        {single ? (
          <>
            <span className="shrink-0 text-xs font-medium text-fg">
              {waiting ? 'Waiting for background job' : 'Background job'} {oldest.id}
            </span>
            <code className="min-w-0 flex-1 truncate font-mono text-2xs text-fg-tertiary" title={oldest.command}>
              {oldest.command}
            </code>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          >
            <span className="text-xs font-medium text-fg">
              {waiting ? `Waiting for ${jobs.length} background jobs` : `${jobs.length} background jobs running`}
            </span>
            <CaretRightIcon
              size={12}
              weight="bold"
              className={classNames('shrink-0 text-fg-tertiary transition-transform', open && 'rotate-90')}
            />
          </button>
        )}
        <span className="shrink-0 text-2xs text-fg-tertiary tabular-nums">{elapsed}</span>
        <Button variant="ghost" size={7} onClick={() => stop(killJobs)} iconLeft={<StopIcon size={12} weight="fill" />}>
          {single ? 'Stop' : 'Stop all'}
        </Button>
      </div>
      {!single && open && (
        <ul className="flex flex-col gap-1 pt-1.5 pl-4">
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} now={now} killJobs={killJobs} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Background jobs still running in the sandbox, pinned above the composer.
 *
 * A job keeps going after the agent's turn ends, and its report can start a new one — so without
 * this, an idle thread that is about to wake up looks exactly like one that is finished.
 */
export function BackgroundJobsIndicator({ killJobs }: { killJobs: KillJobs }) {
  const jobs = useAgentSessionStore((s) => s.jobs);
  if (jobs.length === 0) return null;
  return <RunningJobsStrip jobs={jobs} killJobs={killJobs} />;
}
